import { useState } from "react";
import { Button } from "@/ui/button";
import { Modal } from "@/ui/modals";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import FilterRow from "@/features/filters-modals/filter-row";
import { MinMaxInput } from "@/ui/min-max-input";
import {
  useWalletDiscoveryFilter,
  WalletDiscoveryFiltersParams,
  getWalletDiscoveryDefaultFilters,
} from "@providers/wallet-discovery-filter/wallet-discovery-filter-provider.tsx";

const WalletsFiltersModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { filters, onFiltersUpdate } = useWalletDiscoveryFilter();
  const [localFilters, setLocalFilters] = useState(filters);

  const handleSubmit = (newFilters: WalletDiscoveryFiltersParams) => {
    onFiltersUpdate(newFilters);
  };

  return (
    <>
      <GradientBorderRadius
        gradient="default-border"
        radius="bigger"
        hover="r-100"
        className="h-full"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
        >
          Filters
        </button>
      </GradientBorderRadius>

      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        className="w-[29.125rem] py-[0.9375rem] px-[0.6250rem]"
        size="29.125rem"
      >
        <Modal.Title title="Filters" className="text-xs font-semibold" />
        <div className="w-full h-full grid grid-cols-[max-content,1fr] gap-y-[0.375rem] gap-x-[0.625rem] text-white text-xs font-semibold">
          <FilterRow title="Invested">
            <MinMaxInput
              layout="row"
              unitSide="left"
              text="$"
              minValue={localFilters?.invested?.min}
              maxValue={localFilters?.invested?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  invested: {
                    ...prev!.invested!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  invested: {
                    ...prev!.invested!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Trades">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.trades?.min}
              maxValue={localFilters?.trades?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  invested: {
                    ...prev!.invested!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  invested: {
                    ...prev!.invested!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Win Rate">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.winRate?.min}
              maxValue={localFilters?.winRate?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  winRate: {
                    ...prev!.winRate!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  winRate: {
                    ...prev!.winRate!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Realized PNL">
            <MinMaxInput
              layout="row"
              unitSide="left"
              text="$"
              minValue={localFilters?.realizedPnl?.min}
              maxValue={localFilters?.realizedPnl?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  realizedPnl: {
                    ...prev!.realizedPnl!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  realizedPnl: {
                    ...prev!.realizedPnl!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Realized ROI">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.realizedPnlPercent?.min}
              maxValue={localFilters?.realizedPnlPercent?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  realizedPnlPercent: {
                    ...prev!.realizedPnlPercent!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  realizedPnlPercent: {
                    ...prev!.realizedPnlPercent!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Unrealized PNL">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.unrealizedPnl?.min}
              maxValue={localFilters?.unrealizedPnl?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  unrealizedPnl: {
                    ...prev!.unrealizedPnl!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  unrealizedPnl: {
                    ...prev!.unrealizedPnl!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Unrealized ROI">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.unrealizedPnlPercent?.min}
              maxValue={localFilters?.unrealizedPnlPercent?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  unrealizedPnlPercent: {
                    ...prev!.unrealizedPnlPercent!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  unrealizedPnlPercent: {
                    ...prev!.unrealizedPnlPercent!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Net PNL">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={localFilters?.netPnl?.min}
              maxValue={localFilters?.netPnl?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  netPnl: {
                    ...prev!.netPnl!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  netPnl: {
                    ...prev!.netPnl!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>

          <FilterRow title="Net ROI">
            <MinMaxInput
              unitSide="right"
              text="%"
              layout="row"
              minValue={localFilters?.netPnlPercent?.min}
              maxValue={localFilters?.netPnlPercent?.max}
              onMinChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  netPnlPercent: {
                    ...prev!.netPnlPercent!,
                    min: Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: WalletDiscoveryFiltersParams) => ({
                  ...prev!,
                  netPnlPercent: {
                    ...prev!.netPnlPercent!,
                    max: Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
        </div>
        <Modal.Footer>
          <Button
            title="Done"
            className="w-[9.4375rem] font-semibold text-xs"
            active={false}
            onClick={() => {
              handleSubmit(localFilters);
              setIsOpen(false);
            }}
          />

          <Button
            title="Clear"
            className="w-[9.4375rem] font-semibold text-xs"
            active={false}
            onClick={() => {
              const defaultValue = getWalletDiscoveryDefaultFilters();
              setLocalFilters(defaultValue);
              handleSubmit(defaultValue);
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WalletsFiltersModal;
